import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

interface DataPoint {
  year: string
  value: number | null
  average: number | null
}

interface ChartComponentProps {
  data: DataPoint[]
  grade: string
  subject?: string
  isMeritValue?: boolean
  schoolName: string
  yAxisLabel?: string
}

export default function ChartComponent({
  data,
  grade,
  isMeritValue = false,
  schoolName,
  yAxisLabel,
}: ChartComponentProps) {
  // Check if there's any school data
  const hasSchoolData = data.some((d) => d.value !== null)

  if (!hasSchoolData) {
    return <p>Ingen data tillgänglig för denna skola.</p>
  }

  // Find the minimum and maximum values in the data
  const allValues = data.flatMap((d) => [d.value, d.average]).filter((v): v is number => v !== null && !isNaN(v))
  const minValue = Math.min(...allValues)
  const maxValue = Math.max(...allValues)

  // Calculate the y-axis range based on the grade or if it's a merit value
  const yAxisRange = isMeritValue
    ? [Math.max(0, Math.floor(minValue / 10) * 10 - 10), 340]
    : grade === "3"
      ? [Math.max(Math.floor(minValue / 10) * 10, 80), 100]
      : [Math.max(0, Math.floor(minValue) - 1), Math.ceil(maxValue) + 1]

  // Format the tick values
  const formatYAxis = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(value)) return ""
    const numValue = Number(value)
    if (isMeritValue) return numValue.toFixed(0)
    return grade === "3" ? `${numValue}%` : numValue.toFixed(1)
  }

  // Generate ticks for the y-axis
  const generateTicks = (min: number, max: number, count: number) => {
    const step = (max - min) / (count - 1)
    return Array.from({ length: count }, (_, i) => min + i * step)
  }

  const yAxisTicks = isMeritValue
    ? generateTicks(yAxisRange[0], yAxisRange[1], 6)
    : grade === "3"
      ? generateTicks(yAxisRange[0], yAxisRange[1], 5)
      : generateTicks(yAxisRange[0], yAxisRange[1], 5)

  const yAxisLabelStyle = {
    textAnchor: "start",
    fill: "#666",
    fontSize: "12px",
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={data}
        margin={{
          top: 30,
          right: 30,
          left: 40,
          bottom: 5,
        }}
      >
        {yAxisLabel && (
          <text x={0} y={20} style={yAxisLabelStyle}>
            {yAxisLabel}
          </text>
        )}
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => `${value.slice(0, 2)}/${value.slice(2)}`}
        />
        <YAxis
          domain={yAxisRange}
          tick={{ fontSize: 12 }}
          tickFormatter={formatYAxis}
          ticks={yAxisTicks}
          allowDataOverflow={false}
        />
        <Tooltip
          formatter={(value: number | null | undefined) => {
            if (value === null || value === undefined || isNaN(value))
              return ["N/A", isMeritValue ? "Meritvärde" : "Resultat"]
            return [formatYAxis(value), isMeritValue ? "Meritvärde" : "Resultat"]
          }}
          labelFormatter={(label: string) => `${label.slice(0, 2)}/${label.slice(2)}`}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {data.some((d) => d.value !== null) && (
          <Line
            type="monotone"
            dataKey="value"
            name={isMeritValue ? `${schoolName} meritvärde` : schoolName}
            stroke="#8884d8"
            activeDot={{ r: 8 }}
          />
        )}
        <Line
          type="monotone"
          dataKey="average"
          name="Genomsnitt"
          stroke="#82ca9d"
          dot={{ r: 4 }}
          activeDot={{ r: 8 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}